/**
 * @fileoverview Presentational card for a single AI web search report.
 * Shared by the Searches > Web tab and the Overview background-check card.
 */

import DOMPurify from "dompurify";
import { Loader2, AlertCircle, ExternalLink, FileText } from "lucide-react";
import { Button } from "@/ui/primitives/button";

export interface WebSearchReportCardData {
  _id: string;
  status: "running" | "completed" | "failed";
  subject: string;
  reportHtml?: string;
  sections?: Array<{ heading: string; body: string }>;
  sources?: Array<{ title: string; url: string }>;
  resultDocumentId?: string | null;
  errorMessage?: string;
  completedAt?: number;
}

function sanitize(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "strong", "em", "a", "ul", "ol", "li", "br"],
    ALLOWED_ATTR: ["href", "target", "rel"],
  });
}

export function WebSearchReportCard({
  report,
  onViewPdf,
}: {
  report: WebSearchReportCardData;
  onViewPdf?: (documentId: string) => void;
}) {
  const docId = report.resultDocumentId;

  if (report.status === "running") {
    return (
      <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-4 py-6 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Researching {report.subject} on the web…
      </div>
    );
  }

  if (report.status === "failed") {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
        <AlertCircle className="mt-0.5 size-4 shrink-0" />
        <div>
          <div className="font-medium">Web search failed</div>
          <div className="text-xs opacity-90">
            {report.errorMessage ?? "Unknown error"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-lg border bg-background p-5">
      {report.reportHtml ? (
        <div
          className="prose prose-sm max-w-none text-sm leading-6"
          // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- sanitize() (DOMPurify with tag/attr allow-list, top of file) applied inline
          dangerouslySetInnerHTML={{ __html: sanitize(report.reportHtml) }}
        />
      ) : null}

      {report.sections?.map((s, i) => (
        <div key={i} className="space-y-1">
          <h4 className="text-sm font-semibold">{s.heading}</h4>
          <p className="whitespace-pre-line text-sm leading-6 text-muted-foreground">
            {s.body}
          </p>
        </div>
      ))}

      {report.sources?.length ? (
        <div className="space-y-1.5 border-t pt-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Sources
          </h4>
          <ul className="space-y-1">
            {report.sources.map((src, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs">
                <ExternalLink className="mt-0.5 size-3 shrink-0 text-muted-foreground" />
                <a
                  href={src.url}
                  target="_blank"
                  rel="noreferrer"
                  className="break-all text-primary underline underline-offset-2"
                >
                  {src.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {docId && onViewPdf ? (
        <div className="border-t pt-3">
          <Button variant="outline" size="sm" onClick={() => onViewPdf(docId)}>
            <FileText className="size-4" /> View PDF
          </Button>
        </div>
      ) : null}
    </div>
  );
}
